import React from "react";
import { useNavigate } from "react-router-dom";
import "./LegalPages.css";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <section className="legal-page">
      <button className="home-button" onClick={() => navigate("/")}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </button>

      <div className="legal-inner">
        <h1>PRIVACY POLICY</h1>
        <p className="legal-updated">Last updated: Nov 25, 2025</p>

        <p>
          This Privacy Policy explains how wrappr (&quot;wrappr,&quot;
          &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses,
          and protects your information when you use our mobile application, our
          website at www.wrappr.ai, and related services (collectively, the
          &quot;Service&quot;).
        </p>

        <p>
          By accessing or using the Service, you agree to the collection and use
          of information in accordance with this Privacy Policy. If you do not
          agree with this policy, please do not use the Service.
        </p>

        <h2>1. INFORMATION WE COLLECT</h2>
        <p>
          We collect the following categories of information when you use
          wrappr:
        </p>
        <ul>
          <li>
            <strong>Account Information:</strong> When you create an account, we
            may collect your name, username/handle, email address, password, and
            any other information you choose to share in your profile.
          </li>
          <li>
            <strong>Payment Information:</strong> If you purchase a
            subscription, our payment processor (such as Stripe, Apple, or
            another third-party provider) will collect billing details such as
            your card number, billing address, and payment method. We do not
            store full payment card numbers on our servers.
          </li>
          <li>
            <strong>Content You Create:</strong> This includes prompts, scripts,
            recordings, uploaded audio, text you enter, and any other materials
            you provide for the purpose of generating podcasts and audio content
            using our AI features.
          </li>
          <li>
            <strong>Photos &amp; Media (Photo Library):</strong> If you choose
            to upload images (for example, a profile photo or podcast cover
            image), we access only the specific photos or media you select from
            your device&apos;s library for that purpose. We do not scan or
            access your entire photo library.
          </li>
          <li>
            <strong>Communications:</strong> If you contact us (for example, via
            email or in-app support), we collect the content of your message and
            your contact details.
          </li>
          <li>
            <strong>Usage Data:</strong> We collect information about how you
            use the Service, such as the features you interact with, the type
            and length of audio generated, timestamps, and in-app actions.
          </li>
          <li>
            <strong>Device and Technical Data:</strong> We may collect your IP
            address, browser type, operating system, device identifiers, and app
            version to help us maintain security and improve performance.
          </li>
          <li>
            <strong>Cookies &amp; Similar Technologies:</strong> We may use
            cookies, local storage, and similar technologies to remember your
            preferences, keep you signed in, and analyze how the Service is
            used.
          </li>
        </ul>

        <h3>1.1 Information from Third Parties</h3>
        <ul>
          <li>
            <strong>Authentication Providers:</strong> If you choose to sign in
            through a third-party service (such as Google), we may receive basic
            profile information and an email address from that service, in
            accordance with their terms and your settings.
          </li>
          <li>
            <strong>Service Providers &amp; Partners:</strong> We may receive
            aggregated or technical data from analytics providers, payment
            processors, fraud prevention tools, and other vendors that help us
            operate our business.
          </li>
        </ul>

        <h2>2. HOW WE USE YOUR INFORMATION</h2>
        <p>We use the information we collect for purposes including:</p>
        <ul>
          <li>Operating, maintaining, and improving the Service.</li>
          <li>
            Generating audio and other outputs using our AI features and
            delivering those outputs to you.
          </li>
          <li>
            Allowing you to upload and manage assets such as profile photos or
            podcast cover images, including when you choose to grant access to
            your photo library for that purpose.
          </li>
          <li>
            Personalizing your experience, such as remembering your settings,
            preferences, and recently used projects.
          </li>
          <li>
            Processing transactions, managing subscriptions, and providing
            customer support.
          </li>
          <li>
            Monitoring usage, troubleshooting issues, and protecting the
            security and integrity of the Service.
          </li>
          <li>
            Analyzing performance and usage trends to improve features and user
            experience, including using aggregated or anonymized data.
          </li>
          <li>
            Improving the quality, safety, and performance of our AI models and
            audio-generation capabilities, including by using aggregated,
            anonymized, or de-identified data derived from your use of the
            Service.
          </li>
          <li>
            Communicating with you about updates, new features, security alerts,
            and administrative messages.
          </li>
          <li>
            Complying with legal obligations and enforcing our Terms of Service.
          </li>
        </ul>

        <p>
          We may use aggregated, anonymized, or otherwise de-identified
          information (that does not identify you) to improve our models and
          features, enhance audio quality, relevance, and overall user
          experience. Where permitted by law, this may include using
          de-identified User Content and output internally to train, evaluate,
          and improve our systems. We do not train public or third-party models
          on identifiable user content.
        </p>

        <h2>3. HOW WE SHARE INFORMATION</h2>
        <p>
          We do not sell your personal information. We may share your
          information only in the following circumstances:
        </p>
        <ul>
          <li>
            <strong>Service Providers:</strong> We may share information with
            trusted third-party vendors who help us operate the Service, such as
            cloud hosting providers, analytics services, payment processors, and
            customer support tools. These providers are authorized to use your
            information only as necessary to provide services to us.
          </li>
          <li>
            <strong>Infrastructure &amp; AI Providers:</strong> To generate AI
            podcasts and audio, we may send your prompts, scripts, and relevant
            context to external AI or speech processing providers, in accordance
            with their terms and our agreements with them.
          </li>
          <li>
            <strong>Legal Requirements &amp; Protections:</strong> We may
            disclose information if we believe it is reasonably necessary to
            comply with a law, regulation, legal process, or governmental
            request, or to protect the rights, property, or safety of wrappr,
            our users, or others.
          </li>
          <li>
            <strong>Business Transfers:</strong> In connection with a merger,
            acquisition, financing, or sale of all or a portion of our assets,
            your information may be transferred as part of that transaction,
            subject to standard confidentiality obligations.
          </li>
          <li>
            <strong>With Your Consent:</strong> We may share your information
            for any other purpose disclosed to you and only with your consent.
          </li>
        </ul>

        <h2>4. DATA RETENTION</h2>
        <p>
          We retain your information for as long as your account is active or as
          needed to provide the Service, comply with our legal obligations,
          resolve disputes, and enforce our agreements.
        </p>
        <p>
          When you delete your account (see the <em>Account Deletion</em>{" "}
          section below), we remove or de-identify personal information from our
          active systems where required and technically feasible. We may retain
          certain limited information in a restricted internal archive for
          legitimate business purposes, such as security, fraud prevention,
          recordkeeping, and legal compliance.
        </p>
        <p>
          We may retain certain aggregated, anonymized, or de-identified data
          that does not identify you for analytics, reporting, product
          improvement, and internal model training and evaluation.
        </p>

        <h2>5. ACCOUNT DELETION</h2>
        <p>
          You can request deletion of your wrappr account directly within the
          app by using the in-app account settings (for example, by selecting
          the &quot;Delete Account&quot; option in your profile or settings
          screen). When you initiate account deletion:
        </p>
        <ul>
          <li>
            Your account credentials and profile information are removed from
            our active systems.
          </li>
          <li>
            Where applicable, public-facing content (such as public profiles or
            public podcasts associated with your account) is unpublished or
            removed from user-facing areas of the Service.
          </li>
          <li>
            We may retain certain information in a restricted internal archive,
            including generated podcasts, usage logs, and technical records, for
            legitimate business purposes such as security, abuse and fraud
            prevention, legal compliance, and internal analytics.
          </li>
          <li>
            Archived data is not available to other users, is not used to
            recreate or restore your deleted account, and is not sold or shared
            with third parties for their own marketing purposes.
          </li>
          <li>
            Anonymized, aggregated, or de-identified data derived from your use
            of the Service (including from generated podcasts and other content)
            may be retained and used internally to improve the Service,
            including training and evaluating our models and
            audio-generation capabilities.
          </li>
        </ul>
        <p>
          Most deletions are processed promptly, but certain backups or archives
          may take additional time to fully purge due to technical and legal
          requirements.
        </p>
        <p>
          If you have questions about account deletion or would like to request
          additional deletion of archived data where legally and technically
          possible, you may contact us using the information in the{" "}
          <em>Contact Us</em> section below.
        </p>

        <h2>6. YOUR CHOICES &amp; RIGHTS</h2>
        <ul>
          <li>
            <strong>Account Information:</strong> You can update certain account
            details directly in the app or through your account settings.
          </li>
          <li>
            <strong>Access, Correction &amp; Deletion:</strong> Depending on
            your location, you may have the right to request access to,
            correction of, or deletion of your personal information. You can
            contact us using the details in the <em>Contact Us</em> section
            below to make such a request. We may need to verify your identity
            before responding.
          </li>
          <li>
            <strong>In-App Account Deletion:</strong> In addition to any legal
            rights you may have, you can delete your account directly in the
            app, as described in the <em>Account Deletion</em> section above.
          </li>
          <li>
            <strong>Marketing Communications:</strong> You can opt out of
            marketing emails by using the unsubscribe link in those emails. We
            may still send you non-marketing messages, such as security alerts
            or updates about your account.
          </li>
          <li>
            <strong>Cookies:</strong> Most browsers allow you to control or
            block cookies through their settings. Some parts of the Service may
            not function properly if you disable cookies.
          </li>
        </ul>

        <h2>7. DATA SECURITY</h2>
        <p>
          We implement reasonable technical and organizational measures designed
          to protect your information from unauthorized access, loss, misuse, or
          alteration. However, no method of transmission over the internet or
          electronic storage is completely secure, and we cannot guarantee
          absolute security.
        </p>

        <h2>8. THIRD-PARTY LINKS &amp; SERVICES</h2>
        <p>
          The Service may contain links to third-party websites, services, or
          integrations that are not owned or controlled by wrappr. This Privacy
          Policy does not apply to those third-party services, and we are not
          responsible for their privacy practices. We encourage you to review
          the privacy policies of any third-party services you use.
        </p>

        <h2>9. CHILDREN&apos;S PRIVACY</h2>
        <p>
          The Service is not directed to children under the age of 13 (or a
          higher minimum age in your jurisdiction, for example in parts of the
          EU). We do not knowingly collect personal information from children.
          If we become aware that a child has provided us with personal
          information, we will take steps to delete such information. If you
          believe a child has provided personal data to us, please contact us.
        </p>

        <h2>10. INTERNATIONAL DATA TRANSFERS</h2>
        <p>
          Depending on where you are located, your information may be processed
          and stored in countries that may have different data protection laws
          than your jurisdiction, including the United States. Where required,
          we take steps to ensure that appropriate safeguards are in place to
          protect your information, such as using Standard Contractual Clauses
          or similar mechanisms for international transfers.
        </p>

        <h2>11. CHANGES TO THIS PRIVACY POLICY</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we
          will revise the &quot;Last updated&quot; date at the top of this page.
          In some cases, we may provide additional notice (such as a banner in
          the app or an email). Your continued use of the Service after the
          updated policy becomes effective means you accept the changes.
        </p>

        <h2>12. ADDITIONAL INFORMATION FOR EEA &amp; UK USERS (GDPR)</h2>
        <p>
          If you are located in the European Economic Area (EEA) or the United
          Kingdom, wrappr is the data controller of your personal data.
        </p>
        <p>We process your personal data on the following legal bases:</p>
        <ul>
          <li>
            <strong>Contract:</strong> To provide and operate the Service,
            including creating and managing your account, processing payments,
            and delivering features you use.
          </li>
          <li>
            <strong>Legitimate Interests:</strong> To improve the Service, ensure
            security, prevent fraud, and understand how users interact with the
            Service, provided these interests are not overridden by your rights
            and interests.
          </li>
          <li>
            <strong>Consent:</strong> For certain optional processing (such as
            some marketing communications or non-essential cookies) where
            required by law. You may withdraw consent at any time.
          </li>
          <li>
            <strong>Legal Obligations:</strong> To comply with applicable laws
            and regulations.
          </li>
        </ul>
        <p>If you are in the EEA or UK, you may have the right to:</p>
        <ul>
          <li>Request access to your personal data.</li>
          <li>Request correction of inaccurate or incomplete personal data.</li>
          <li>Request deletion of your personal data.</li>
          <li>Request restriction of processing of your personal data.</li>
          <li>
            Object to certain processing, including profiling based on
            legitimate interests.
          </li>
          <li>
            Request data portability, where technically feasible, for data you
            have provided to us.
          </li>
          <li>Withdraw consent where processing is based on consent.</li>
        </ul>
        <p>
          You also have the right to lodge a complaint with your local data
          protection authority if you believe our processing of your personal
          data violates applicable law.
        </p>

        <h2>13. CALIFORNIA PRIVACY RIGHTS (CCPA/CPRA)</h2>
        <p>
          If you are a California resident, you may have certain rights under
          the California Consumer Privacy Act (&quot;CCPA&quot;), as amended by
          the California Privacy Rights Act (&quot;CPRA&quot;).
        </p>
        <p>You may have the right to:</p>
        <ul>
          <li>
            Request to know the categories and specific pieces of personal
            information we collect, use, disclose, and share.
          </li>
          <li>
            Request deletion of your personal information, subject to certain
            exceptions.
          </li>
          <li>
            Request correction of inaccurate personal information that we
            maintain about you.
          </li>
          <li>
            Opt out of the &quot;sale&quot; or &quot;sharing&quot; of your
            personal information. wrappr does not sell your personal information
            as defined under the CCPA/CPRA.
          </li>
          <li>
            Not be discriminated against for exercising any of your CCPA/CPRA
            rights.
          </li>
        </ul>
        <p>
          To exercise your rights, you can contact us using the details below.
          We may need to verify your identity before responding to your request.
        </p>

        <h2>14. CONTACT US</h2>
        <p>
          If you have any questions about this Privacy Policy or our privacy
          practices, you can contact us at:
        </p>
        <p>
          <strong>wrappr</strong>
          <br />
          Website:{" "}
          <a href="https://www.wrappr.ai" target="_blank" rel="noreferrer">
            www.wrappr.ai
          </a>
          <br />
          Email: <a href="mailto:hello@wrappr.ai">hello@wrappr.ai</a>
        </p>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
